import { Link } from '@tanstack/react-router'
import {
  Button,
  Menu,
  MenuItem,
  Text as MenuText,
  MenuTrigger,
} from '@react-spectrum/s2'
import Delete from '@react-spectrum/s2/icons/Delete'
import Add from '@react-spectrum/s2/icons/Add'
import { Ellipsis, MessageSquare } from 'lucide-react'
import { trackAnalyticsEvent } from '@client/analytics'
import { fullWidthCenteredButtonStyles } from '@/styles/s2-macros'
import { cn } from '@/lib/utils'

interface MobileChatHistoryProps {
  history: Array<any>
  activeConversationId?: string
  onNewChat: () => void
  onSelect: () => void
  onDeleteConversation: (conversationId: string) => void
  showTitle?: boolean
  showNewChatButton?: boolean
  alwaysShowActions?: boolean
}

export function MobileChatHistory({
  history,
  activeConversationId,
  onNewChat,
  onSelect,
  onDeleteConversation,
  showTitle = true,
  showNewChatButton = true,
  alwaysShowActions = false,
}: MobileChatHistoryProps) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {showTitle ? (
        <div className="px-4 pb-4 pt-4">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
            History
          </h2>
        </div>
      ) : null}

      {showNewChatButton ? (
        <div className="px-4 pb-4 pt-3">
          <Button
            type="button"
            variant="secondary"
            fillStyle="outline"
            size="M"
            styles={fullWidthCenteredButtonStyles}
            onPress={() => {
              trackAnalyticsEvent('new_chat_clicked', {
                location: 'mobile_history',
                history_count: history.length,
              })
              onNewChat()
              onSelect()
            }}
          >
            <Add />
            New chat
          </Button>
        </div>
      ) : null}

      <div className="flex-1 space-y-1 overflow-y-auto overscroll-contain px-4 pb-4">
        <div className="mb-2 px-2 text-xs font-medium text-muted-foreground">
          Recent chats
        </div>
        {history.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No history yet.
          </p>
        )}
        {history.map((chat) => (
          <div
            key={chat.id}
            className={cn(
              'group flex items-center gap-1 rounded-md transition-colors',
              activeConversationId === chat.id
                ? 'bg-secondary'
                : 'hover:bg-secondary/60',
            )}
          >
            <Link
              to="/chat"
              search={{ conversationId: chat.id }}
              onClick={() => {
                trackAnalyticsEvent('conversation_selected', {
                  conversation_id: chat.id,
                  location: 'mobile_history',
                  is_active: activeConversationId === chat.id,
                })
                onSelect()
              }}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 truncate p-3 text-left text-sm transition-colors',
                activeConversationId === chat.id
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                <span className="block truncate">
                  {chat.title || 'Untitled chat'}
                </span>
              </span>
            </Link>
            <MenuTrigger align="end">
              <div
                className={cn(
                  'mr-2 transition-opacity',
                  alwaysShowActions
                    ? ''
                    : 'invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100',
                )}
              >
                <Button
                  variant="secondary"
                  fillStyle="outline"
                  size="S"
                  aria-label={`Open conversation actions for ${chat.title || 'Untitled chat'}`}
                >
                  <Ellipsis className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Menu
                aria-label="Conversation actions"
                size="M"
                className="min-w-[10rem]"
              >
                <MenuItem
                  onAction={() => {
                    trackAnalyticsEvent('conversation_deleted', {
                      conversation_id: chat.id,
                      location: 'mobile_history',
                      is_active: activeConversationId === chat.id,
                    })
                    onDeleteConversation(chat.id)
                  }}
                >
                  <Delete />
                  <MenuText slot="label">Delete</MenuText>
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
        ))}
      </div>
    </div>
  )
}
